<template>
  <div class="public-survey-page" :class="pageTheme">
    <div class="ps-bg-pattern"></div>
    <div class="survey-shell">
      <div class="ps-brand">
        <span class="ps-brand-dot"></span>
        CHED Caraga
      </div>

      <div v-if="loading" class="ps-card ps-state">
        <div class="spinner-border text-primary"></div>
        <p class="mt-3 mb-0">Loading survey...</p>
      </div>

      <div v-else-if="error" class="ps-card ps-state ps-error">
        <div class="state-icon error"><i class="bi bi-exclamation-circle"></i></div>
        <h5>Survey Unavailable</h5>
        <p class="mb-0">{{ error }}</p>
      </div>

      <div v-else-if="submitted" class="ps-card ps-state ps-success">
        <div class="state-icon success"><i class="bi bi-check-circle-fill"></i></div>
        <h5>Thank you for your feedback!</h5>
        <p class="mb-0">Your response has been recorded successfully.</p>
      </div>

      <form v-else class="ps-card ps-form" @submit.prevent="submit">
        <div class="ps-form-header">
          <div class="ps-badge-row">
            <span class="ps-type-badge">
              <i :class="isOnline ? 'bi bi-camera-video-fill' : 'bi bi-people-fill'"></i>
              {{ surveyTypeLabel }}
            </span>
            <span v-if="instanceLabel" class="ps-instance-badge">
              <i class="bi bi-calendar2-check"></i>
              {{ instanceLabel }}
            </span>
          </div>
          <h4>{{ settings.title }}</h4>
          <p v-if="activityTitle" class="ps-activity">{{ activityTitle }}</p>
          <p v-if="settings.description" class="ps-desc">{{ settings.description }}</p>
          <p v-if="expiresAtLabel" class="ps-expiry">
            <i class="bi bi-clock"></i> Closes {{ expiresAtLabel }}
          </p>
        </div>

        <div v-if="!settings.allow_anonymous" class="form-group">
          <label class="ps-label">Your name</label>
          <input v-model="respondentName" class="form-control ps-input" required maxlength="255" />
        </div>

        <div v-for="(question, idx) in settings.questions" :key="question.id" class="ps-question">
          <label class="ps-label">
            <span class="q-num">{{ idx + 1 }}</span>
            {{ question.label }}
            <span v-if="question.required" class="req">*</span>
          </label>

          <div v-if="question.type === 'rating'" class="rating-grid">
            <button
              v-for="n in settings.rating_scale_max"
              :key="n"
              type="button"
              class="rating-chip"
              :class="{ active: answers[question.id] === n }"
              @click="setRating(question.id, n)"
            >
              {{ n }}
            </button>
          </div>

          <textarea
            v-else
            v-model="answers[question.id]"
            class="form-control ps-input"
            rows="3"
            :required="question.required || (settings.require_comment && question.id === 'feedback')"
            maxlength="2000"
            placeholder="Share your thoughts..."
          ></textarea>
        </div>

        <button type="submit" class="ps-submit" :disabled="submitting">
          <span v-if="submitting" class="spinner-border spinner-border-sm mr-2"></span>
          <i v-else class="bi bi-send-fill mr-2"></i>
          Submit Feedback
        </button>
      </form>

      <p class="ps-footer">Your response is confidential · CHED Caraga</p>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "PublicSurveyPage",
  data() {
    return {
      loading: true,
      submitting: false,
      submitted: false,
      error: null,
      activityTitle: null,
      instanceLabel: null,
      expiresAt: null,
      expiresAtLabel: null,
      surveyTypeLabel: "Post Activity",
      surveyType: "online",
      respondentName: "",
      answers: {},
      settings: {
        title: "",
        description: "",
        rating_scale_max: 5,
        questions: [],
        allow_anonymous: true,
        require_comment: false,
      },
    };
  },
  computed: {
    isOnline() { return this.surveyType === "online"; },
    pageTheme() { return this.isOnline ? "theme-online" : "theme-f2f"; },
  },
  mounted() {
    this.setNoReferrer();
    this.updatePageTitle();
    this.loadSurvey();
  },
  beforeDestroy() {
    this.clearNoReferrer();
  },
  methods: {
    setNoReferrer() {
      // The survey token lives in this page's URL. Without this, clicking any
      // outbound link (or loading a third-party resource) would leak the full
      // URL — token included — to that third party via the Referer header.
      const meta = document.createElement("meta");
      meta.name = "referrer";
      meta.content = "no-referrer";
      meta.setAttribute("data-survey-referrer-policy", "true");
      document.head.appendChild(meta);
    },
    clearNoReferrer() {
      const meta = document.head.querySelector(
        "meta[data-survey-referrer-policy]"
      );
      if (meta) meta.remove();
    },
    respondentStorageKey() {
      return "survey_respondent_id";
    },
    submittedStorageKey(token) {
      return `survey_submitted_${token}`;
    },
    newClientToken() {
      // Prefer Web Crypto; randomUUID is missing in some in-app / HTTP WebViews
      // used when opening survey QR links.
      try {
        if (typeof crypto !== "undefined") {
          if (typeof crypto.randomUUID === "function") {
            return crypto.randomUUID().replace(/-/g, ""); // 32 chars
          }
          if (typeof crypto.getRandomValues === "function") {
            const bytes = new Uint8Array(16);
            crypto.getRandomValues(bytes);
            return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
          }
        }
      } catch (e) {
        /* fall through */
      }
      // Math.random().toString(36) can be very short — pad until >= 16.
      let token = `sr_${Date.now().toString(36)}`;
      while (token.length < 32) {
        token += Math.random().toString(36).slice(2);
      }
      return token.slice(0, 64);
    },
    getOrCreateClientToken() {
      const key = this.respondentStorageKey();
      try {
        let token = localStorage.getItem(key);
        if (token && token.length >= 16 && token.length <= 64) {
          return token;
        }
        token = this.newClientToken();
        localStorage.setItem(key, token);
        return token;
      } catch (e) {
        return this.newClientToken();
      }
    },
    markSubmittedLocally(token) {
      try {
        localStorage.setItem(this.submittedStorageKey(token), "1");
      } catch (e) {
        /* ignore quota / private mode */
      }
    },
    hasSubmittedLocally(token) {
      try {
        return localStorage.getItem(this.submittedStorageKey(token)) === "1";
      } catch (e) {
        return false;
      }
    },
    updatePageTitle() {
      let title = "Activity Survey";
      if (this.submitted) {
        title = "Thank You";
      } else if (this.error) {
        title = "Survey Unavailable";
      } else if (this.settings.title) {
        title = this.settings.title;
      } else if (this.activityTitle) {
        title = this.activityTitle;
      }
      document.title = `${title} | CHED Caraga`;
    },
    async loadSurvey() {
      const token = this.$route.params.token;
      if (!token) {
        this.error = "Invalid survey link.";
        this.loading = false;
        this.updatePageTitle();
        return;
      }
      if (this.hasSubmittedLocally(token)) {
        this.submitted = true;
        this.loading = false;
        this.updatePageTitle();
        return;
      }
      try {
        const { data } = await axios.get(`api/survey/${encodeURIComponent(token)}`);
        if (data.is_expired) {
          this.error = "This survey link has expired.";
          return;
        }
        this.activityTitle = data.activity_title;
        this.instanceLabel = data.instance_label
          || data.survey_name
          || data.session_label
          || null;
        if (this.instanceLabel && /^main survey$/i.test(String(this.instanceLabel).trim())) {
          this.instanceLabel = data.session_label || null;
        }
        this.expiresAt = data.expires_at;
        this.expiresAtLabel = data.expires_at
          ? moment(data.expires_at).format("MMM D, YYYY h:mm A")
          : null;
        this.surveyTypeLabel = data.survey_type_label || "Post Activity";
        this.surveyType = data.survey_type || "online";
        this.settings = data.settings || this.settings;
      } catch (e) {
        if (!e.response) {
          this.error = "Could not reach the survey server. Please check your internet connection and try again.";
        } else if (e.response.status === 404) {
          this.error = e.response.data?.message || "Survey not found or link is invalid.";
        } else {
          this.error = e.response.data?.message || "Unable to load this survey right now.";
        }
      } finally {
        this.loading = false;
        this.updatePageTitle();
      }
    },
    setRating(questionId, value) { this.$set(this.answers, questionId, value); },
    async submit() {
      this.submitting = true;
      const surveyToken = this.$route.params.token;
      const clientToken = this.getOrCreateClientToken();
      try {
        // This page only holds an XSRF-TOKEN cookie when loadSurvey() happened
        // to fetch first, and it does not always run — a locally remembered
        // submission, a missing token and a failed load all skip it. The
        // endpoint still goes through Sanctum's stateful middleware, which
        // answers a tokenless POST with 419 "CSRF token mismatch".
        await axios.get("/sanctum/csrf-cookie");
        await axios.post(`api/survey/${encodeURIComponent(surveyToken)}`, {
          answers: this.answers,
          respondent_name: this.settings.allow_anonymous ? null : this.respondentName,
          client_token: clientToken,
        }, {
          headers: clientToken ? { "X-Survey-Client-Token": clientToken } : undefined,
        });
        this.markSubmittedLocally(surveyToken);
        this.submitted = true;
        this.updatePageTitle();
      } catch (e) {
        toastr.error(e.response?.data?.message || "Failed to submit survey.", "Error");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.public-survey-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  position: relative;
  overflow: hidden;
}

.theme-online {
  background: linear-gradient(160deg, #e8f0ff 0%, #f4f7fc 50%, #ffffff 100%);
}

.theme-f2f {
  background: linear-gradient(160deg, #e6f7f4 0%, #f4faf9 50%, #ffffff 100%);
}

.ps-bg-pattern {
  position: absolute;
  inset: 0;
  opacity: 0.4;
  background-image: radial-gradient(circle at 20% 30%, rgba(77, 141, 255, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(43, 182, 163, 0.06) 0%, transparent 50%);
  pointer-events: none;
}

.survey-shell {
  width: 100%;
  max-width: 480px;
  position: relative;
  z-index: 1;
}

.ps-brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #768192;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 1rem;
  justify-content: center;
}

.ps-brand-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4d8dff;
}

.ps-card {
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 12px 48px rgba(43, 58, 85, 0.1);
  padding: 1.75rem;
}

.ps-state {
  text-align: center;
  padding: 2.5rem 1.75rem;
  color: #768192;
}

.state-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.75rem;
}

.state-icon.success { background: rgba(43, 182, 163, 0.12); color: #2bb6a3; }
.state-icon.error { background: rgba(229, 83, 83, 0.1); color: #e55353; }

.ps-form-header { margin-bottom: 1.5rem; }

.ps-badge-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
}

.ps-type-badge,
.ps-instance-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.25rem 0.65rem;
  border-radius: 8px;
  margin-bottom: 0;
}

.theme-online .ps-type-badge { background: rgba(77, 141, 255, 0.12); color: #4d8dff; }
.theme-f2f .ps-type-badge { background: rgba(43, 182, 163, 0.12); color: #1f8f7f; }

.ps-instance-badge {
  background: rgba(43, 58, 85, 0.08);
  color: #2b3a55;
  text-transform: none;
  letter-spacing: 0.02em;
  font-size: 0.78rem;
}

.ps-form-header h4 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2b3a55;
  margin-bottom: 0.35rem;
}

.ps-activity { font-weight: 600; color: #4a5568; font-size: 0.92rem; margin-bottom: 0.4rem; }
.ps-desc { color: #768192; font-size: 0.88rem; line-height: 1.5; margin-bottom: 0.4rem; }
.ps-expiry { font-size: 0.78rem; color: #9aa5b4; margin: 0; }

.ps-label {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  font-size: 0.88rem;
  font-weight: 600;
  color: #2b3a55;
  margin-bottom: 0.55rem;
}

.q-num {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: #f0f3f8;
  font-size: 0.7rem;
  font-weight: 700;
  color: #768192;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.req { color: #e55353; }

.ps-question { margin-bottom: 1.35rem; }

.ps-input {
  border-radius: 12px;
  border-color: rgba(0, 0, 21, 0.1);
  font-size: 0.9rem;
}

.rating-grid {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.rating-chip {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  border: 2px solid rgba(0, 0, 21, 0.1);
  background: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rating-chip:hover { border-color: #4d8dff; color: #4d8dff; transform: translateY(-1px); }

.theme-online .rating-chip.active {
  background: #4d8dff;
  border-color: #4d8dff;
  color: #fff;
  box-shadow: 0 4px 12px rgba(77, 141, 255, 0.35);
}

.theme-f2f .rating-chip.active {
  background: #2bb6a3;
  border-color: #2bb6a3;
  color: #fff;
  box-shadow: 0 4px 12px rgba(43, 182, 163, 0.35);
}

.ps-submit {
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 0.75rem;
  font-weight: 700;
  font-size: 0.95rem;
  color: #fff;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: opacity 0.15s ease;
}

.theme-online .ps-submit { background: linear-gradient(135deg, #4d8dff, #3a7ae8); }
.theme-f2f .ps-submit { background: linear-gradient(135deg, #2bb6a3, #1f9a8a); }
.ps-submit:disabled { opacity: 0.65; cursor: not-allowed; }

.ps-footer {
  text-align: center;
  font-size: 0.72rem;
  color: #9aa5b4;
  margin-top: 1rem;
  margin-bottom: 0;
}
</style>
